// =============================================================
// admin.ts — AdminService: 역할 판별 + 총판 관리 + 구매자 조회
//  · role():          현재 지갑이 owner / distributor / user 중 무엇인지
//  · listDistributors(): 전체 총판 목록 + 상세
//  · register / setRates / setActive / release / remove (owner)
//  · setMyDiscount (총판 본인)
//  · buyersOf():      특정 총판으로 산 구매자 (온체인 Swapped 이벤트)
// =============================================================

import { Contract } from 'ethers';
import { PV_GATE_ADDRESS, PV_GATE_ABI } from './config';
import { ChainClient } from './chain';
import { SdkError, type WalletAdapter } from './types';

export type WalletRole = 'owner' | 'distributor' | 'user';

export interface DistributorRow {
  address: string;
  active: boolean;
  discountBp: number;
  rewardBp: number;
  ownerSet: boolean;
}

export interface BuyerRow {
  buyer: string;
  bnbIn: bigint;
  molOut: bigint;
  txHash: string;
  blockNumber: number;
}

export class AdminService {
  constructor(
    private wallet: WalletAdapter,
    private chain: ChainClient,
  ) {}

  /** 현재 지갑 역할 판별 */
  async role(): Promise<WalletRole> {
    const me = this.wallet.getAddress();
    if (!me) return 'user';
    try {
      const gate = await this.chain.gateRead();
      const ownerAddr = (await gate.owner()) as string;
      if (ownerAddr.toLowerCase() === me.toLowerCase()) return 'owner';
      const info = (await gate.distributorInfo(me)) as [boolean, boolean, bigint, bigint, boolean];
      if (info[0]) return 'distributor'; // exists
      return 'user';
    } catch {
      return 'user';
    }
  }

  /** owner 주소 */
  async ownerAddress(): Promise<string | null> {
    try {
      const gate = await this.chain.gateRead();
      return (await gate.owner()) as string;
    } catch {
      return null;
    }
  }

  /** 내 총판 정보 (총판 본인용) */
  async myDistributorInfo(): Promise<DistributorRow | null> {
    const me = this.wallet.getAddress();
    if (!me) return null;
    return this.distributorInfo(me);
  }

  async distributorInfo(addr: string): Promise<DistributorRow | null> {
    try {
      const gate = await this.chain.gateRead();
      const r = (await gate.distributorInfo(addr)) as [boolean, boolean, bigint, bigint, boolean];
      if (!r[0]) return null;
      return {
        address: addr,
        active: r[1],
        discountBp: Number(r[2]),
        rewardBp: Number(r[3]),
        ownerSet: r[4],
      };
    } catch {
      return null;
    }
  }

  /** 전체 총판 목록 */
  async listDistributors(): Promise<DistributorRow[]> {
    const gate = await this.chain.gateRead();
    const count = Number((await gate.distributorCount()) as bigint);
    const rows: DistributorRow[] = [];
    for (let i = 0; i < count; i++) {
      const addr = (await gate.distributorAt(i)) as string;
      const info = await this.distributorInfo(addr);
      if (info) rows.push(info);
    }
    return rows;
  }

  // ── owner 전용 ──
  async register(addr: string): Promise<string> {
    const gate = await this.chain.gateWrite();
    const tx = await gate.registerDistributor(addr);
    await tx.wait(1);
    return tx.hash;
  }
  async setRates(addr: string, discountBp: number, rewardBp: number): Promise<string> {
    const gate = await this.chain.gateWrite();
    const tx = await gate.ownerSetRates(addr, discountBp, rewardBp);
    await tx.wait(1);
    return tx.hash;
  }
  async setActive(addr: string, active: boolean): Promise<string> {
    const gate = await this.chain.gateWrite();
    const tx = await gate.setActive(addr, active);
    await tx.wait(1);
    return tx.hash;
  }
  async release(addr: string): Promise<string> {
    const gate = await this.chain.gateWrite();
    const tx = await gate.releaseToDistributor(addr);
    await tx.wait(1);
    return tx.hash;
  }
  async remove(addr: string): Promise<string> {
    const gate = await this.chain.gateWrite();
    const tx = await gate.removeDistributor(addr);
    await tx.wait(1);
    return tx.hash;
  }

  // ── 총판 본인 ──
  async setMyDiscount(discountBp: number): Promise<string> {
    const gate = await this.chain.gateWrite();
    const tx = await gate.setMyDiscount(discountBp);
    await tx.wait(1);
    return tx.hash;
  }
  /** 자동 보상 미리보기 (할인 → 보상) */
  async autoReward(discountBp: number): Promise<number> {
    const gate = await this.chain.gateRead();
    return Number((await gate.autoReward(discountBp)) as bigint);
  }

  /**
   * 특정 총판으로 산 구매자 목록 (온체인 Swapped 이벤트).
   * fromBlock 부터 현재까지. 소규모용 — 많아지면 MariaDB 인덱서로 승격.
   */
  async buyersOf(distributor: string, fromBlock = 0): Promise<BuyerRow[]> {
    if (!PV_GATE_ADDRESS) throw new SdkError('NO_GATE_ADDRESS');
    const provider = await this.wallet.getProvider();
    const gate = new Contract(PV_GATE_ADDRESS, PV_GATE_ABI, provider);
    // Swapped(buyer indexed, ..., distributor indexed, ...)
    const filter = gate.filters.Swapped(null, distributor);
    const logs = await gate.queryFilter(filter, fromBlock, 'latest');
    const rows: BuyerRow[] = [];
    for (const log of logs) {
      const args = (log as unknown as { args: Record<string, unknown> }).args;
      if (!args) continue;
      rows.push({
        buyer: String(args.buyer),
        bnbIn: args.bnbIn as bigint,
        molOut: args.molOut as bigint,
        txHash: (log as unknown as { transactionHash: string }).transactionHash,
        blockNumber: (log as unknown as { blockNumber: number }).blockNumber,
      });
    }
    return rows;
  }

  /** 구매자 수 (고유 지갑) */
  async buyerCountOf(distributor: string, fromBlock = 0): Promise<number> {
    const rows = await this.buyersOf(distributor, fromBlock);
    return new Set(rows.map((r) => r.buyer.toLowerCase())).size;
  }
}
